import clsx from "clsx";
import { Children, useState } from "react";

import { Down16, Up16 } from "@hagerty/icons";

import { Card } from "../Card";

import {
  ListCardFooterProps,
  ListCardHeaderProps,
  ListCardProps,
  ListCardExpandableRowsProps,
  ListCardRowProps,
} from "./ListCard.types";
import styles from "./ListCard.module.scss";

export const ListCard = ({ children, className, ...props }: ListCardProps) => (
  <Card as="section" className={clsx(styles.listCard, className)} {...props}>
    {children}
  </Card>
);

ListCard.Header = ({ children, className, ...props }: ListCardHeaderProps) => (
  <header className={clsx(styles.header, className)} {...props}>
    {children}
  </header>
);

ListCard.Row = ({ children, className, ...props }: ListCardRowProps) => (
  <div className={clsx(styles.row, className)} {...props}>
    {children}
  </div>
);

ListCard.Footer = ({ children, className, ...props }: ListCardFooterProps) => (
  <footer className={clsx(styles.footer, className)} {...props}>
    {children}
  </footer>
);

const ExpandableRows = ({
  children,
  className,
  maxRowCount,
  viewRowsLabel,
  hideRowsLabel,
  hasFooter,
  ...props
}: ListCardExpandableRowsProps) => {
  const childrenList = Children.toArray(children);
  const [hideRows, setHideRows] = useState(true);
  const childrenExceedMaxRowCount = childrenList.length > maxRowCount;

  return (
    <div className={clsx(styles.expandableRows, className)} {...props}>
      <div
        className={clsx(
          (childrenExceedMaxRowCount || !hasFooter) && styles.hideLastRowBorder
        )}
      >
        {childrenList.map((child, index) =>
          hideRows && index >= maxRowCount ? null : child
        )}
      </div>
      {hideRows && childrenExceedMaxRowCount && (
        <>
          <div className={styles.moreChildrenRow}>
            <div className={styles.hiddenRowCount}>
              +{childrenList.length - maxRowCount} more
            </div>
            <button
              aria-label={viewRowsLabel}
              className={styles.action}
              onClick={() => setHideRows(false)}
            >
              {viewRowsLabel} <Down16 className={styles.viewMoreLessIcon} />
            </button>
          </div>
          {!hasFooter && <div className={styles.spacer} />}
        </>
      )}
      {!hideRows && childrenExceedMaxRowCount && (
        <>
          <div className={styles.lessChildrenRow}>
            <button
              aria-label={hideRowsLabel}
              className={styles.action}
              onClick={() => setHideRows(true)}
            >
              {hideRowsLabel} <Up16 className={styles.viewMoreLessIcon} />
            </button>
          </div>
          {!hasFooter && <div className={styles.spacer} />}
        </>
      )}
    </div>
  );
};

ListCard.ExpandableRows = ExpandableRows;
